import { AuthClientError } from '@/services/auth-client/auth-error';
import type {
  AccountsClient,
  AuthClient,
  ProfileRow,
  VolunteerFunctionRow,
} from '@/services/auth-client/types';

import {
  EMPTY_STORED_PERSONAL_DATA,
  hasCompletePersonalData,
  mapRowToStoredPersonalData,
  toPersonalDataRow,
} from '../lib/personal-data';

import type { StoredPersonalData } from '../lib/personal-data';
import type {
  Account,
  BecomeVolunteerInput,
  NewAccountInput,
  Session,
  VolunteerFunction,
} from '../types';

/**
 * Service de contas: login, cadastro, sessão, recuperação de senha e o passo
 * opcional de voluntariado.
 *
 * Não importa React e não fala com o usuário — erro sobe por `throw` e é
 * traduzido uma vez só, na borda (docs/04-estado-e-dados.md). As dependências
 * entram por parâmetro e são ligadas em `src/services/index.ts`, o único lugar do
 * app que sabe qual adapter está no ar.
 */

export interface AuthServiceDeps {
  auth: AuthClient;
  accounts: AccountsClient;
}

/**
 * A coluna nasce `null` e o fallback visual é da interface: uma URL externa
 * gravada em toda linha de `profiles` tornaria o avatar de todo mundo refém de
 * um domínio de terceiro.
 */
export const DEFAULT_PROFILE_PHOTO_URL = null;

/**
 * O nome que a conta anônima carrega até a pessoa terminar o cadastro (DV-24).
 *
 * `profiles.display_name` é `not null`, e a pessoa que abre um SOS sem conta não
 * digitou nome nenhum — ela digitou um e-mail de contato. Poderia ser string
 * vazia; não é, porque este nome **aparece** para quem vai atender, no lugar de
 * "quem pediu". Dizer que o pedido veio de alguém sem cadastro é informação
 * verdadeira e útil para quem despacha, e some sozinha quando a pessoa conclui o
 * cadastro e escolhe como quer ser chamada.
 */
export const GUEST_DISPLAY_NAME = 'Pedido sem cadastro';

/**
 * Mapeamento linha → domínio. Função pura, exportada e testável sem rede — é o
 * que docs/04 exige.
 */
export function mapRowToAccount(profile: ProfileRow): Account {
  return {
    id: profile.id,
    displayName: profile.display_name,
    email: profile.email,
    phoneNumber: profile.phone_number,
    age: profile.age,
    gender: profile.gender,
    photoUrl: profile.photo_url,
    isVolunteer: profile.is_volunteer,
    createdAt: new Date(profile.created_at),
  };
}

/**
 * O estado de voluntariado que o passo abre preenchido. `isVolunteer` está aqui
 * só para a tela escolher as palavras: quem já é voluntário está **alterando
 * funções**, e ler "Quer atuar como voluntário?" nesse momento sugere que o app
 * esqueceu quem ela é.
 */
export interface VolunteerDefaults {
  functionIds: string[];
  isVolunteer: boolean;
  /**
   * O perfil ainda não tem os dados pessoais que a conta voluntária exige
   * (DV-25) — é o solicitante que resolveu virar voluntário. O passo os pede
   * nesse caso, e só nele.
   */
  needsPersonalData: boolean;
  /** O que já está gravado, para o bloco de dados pessoais abrir preenchido. */
  personal: StoredPersonalData;
}

export function mapRowToVolunteerFunction(row: VolunteerFunctionRow): VolunteerFunction {
  return { id: row.id, slug: row.slug, name: row.name };
}

export const createAuthService = ({ auth, accounts }: AuthServiceDeps) => ({
  async getSession(): Promise<Session | null> {
    const user = await auth.getCurrentUser();

    return user ? { userId: user.id, email: user.email, isAnonymous: user.isAnonymous } : null;
  },

  /** `null` quando não há sessão, ou quando a sessão ainda não tem perfil. */
  async getCurrentAccount(): Promise<Account | null> {
    const user = await auth.getCurrentUser();

    if (!user) return null;

    const profile = await accounts.findProfile(user.id);

    return profile ? mapRowToAccount(profile) : null;
  },

  /**
   * O que o passo de voluntário precisa saber sobre quem já está logado: os
   * dados pessoais que o perfil tem (e se faltam) e as funções já escolhidas.
   *
   * Existe por causa do segundo ponto de entrada. Vindo do cadastro não há nada
   * a preservar; vindo do Perfil ("Alterar funções") há — e como a gravação
   * **substitui** o conjunto inteiro, abrir a tela sem esses valores apagaria as
   * funções da pessoa.
   */
  async getVolunteerDefaults(): Promise<VolunteerDefaults | null> {
    const user = await auth.getCurrentUser();

    if (!user) return null;

    const [profile, functionIds] = await Promise.all([
      accounts.findProfile(user.id),
      accounts.listProfileFunctionIds(user.id),
    ]);

    return {
      functionIds,
      isVolunteer: profile?.is_volunteer ?? false,
      needsPersonalData: profile ? !hasCompletePersonalData(profile) : true,
      personal: profile ? mapRowToStoredPersonalData(profile) : EMPTY_STORED_PERSONAL_DATA,
    };
  },

  async signIn(email: string, password: string): Promise<Session> {
    const user = await auth.signInWithPassword({ email, password });

    return { userId: user.id, email: user.email, isAnonymous: false };
  },

  /**
   * Abre a conta anônima que vai segurar um pedido de socorro feito sem cadastro
   * (DV-24), já com o e-mail de contato informado no formulário.
   *
   * ## Por que existe uma conta, se a pessoa não quis se cadastrar
   *
   * `help_requests.author_id` referencia `profiles`: pedido sem dono não é
   * representável no banco, e afrouxar isso para aceitar `null` significaria
   * reescrever toda policy que hoje responde "é seu?" — incluindo a que decide
   * quem enxerga dado de vítima. A conta anônima mantém o modelo intacto e dá a
   * quem pediu algo que um pedido órfão nunca daria: o direito de acompanhar,
   * cancelar e, depois, reivindicar o próprio pedido.
   *
   * ## O e-mail entra no perfil, não no pedido
   *
   * Ele é identidade em formação, não contato de resgate: quem vai atender usa o
   * telefone que o passo 3 pede, como em qualquer outro pedido. Gravá-lo também
   * em `help_requests` ampliaria o dado de vítima exposto (DV-03) sem ninguém ter
   * pedido por isso.
   *
   * O par `signInAnonymously` + gravação do perfil fica fora de transação pela
   * mesma razão que o cadastro comum: são sistemas diferentes. Falhando a
   * segunda, sobra sessão sem perfil — estado que `getCurrentAccount` já devolve
   * como `null` e que o próximo envio corrige.
   */
  async startGuestAccount(contactEmail: string): Promise<Account> {
    await auth.signInAnonymously();

    const profile = await accounts.createAccount({
      display_name: GUEST_DISPLAY_NAME,
      email: contactEmail,
      phone_number: null,
      age: null,
      gender: null,
      photo_url: DEFAULT_PROFILE_PHOTO_URL,
      is_volunteer: false,
    });

    return mapRowToAccount(profile);
  },

  /**
   * Manda o link mágico que termina o cadastro da conta anônima.
   *
   * O e-mail sai **daqui**, e não de dentro de `startGuestAccount`: no meio de um
   * pedido de socorro, ninguém vai ler caixa de entrada. A pessoa envia o
   * pedido, vê a confirmação, e só então decide se quer virar conta — é a ordem
   * que o produto pediu, e também a que evita disparar e-mail para quem nunca
   * quis um.
   */
  async sendSignUpLink(email: string, redirectTo: string): Promise<void> {
    await auth.sendSignUpLink(email, redirectTo);
  },

  /**
   * Último passo do cadastro de quem entrou pelo socorro: senha e nome.
   *
   * O e-mail já foi confirmado pelo link — é o que tira a marca de anônima da
   * sessão. Aqui só falta a conta passar a entrar pelo mesmo caminho de todo
   * mundo (e-mail + senha) e a pessoa deixar de se chamar
   * `GUEST_DISPLAY_NAME` para quem atende.
   */
  async finishSignUp(input: { displayName: string; password: string }): Promise<Account> {
    const user = await auth.getCurrentUser();

    if (!user) throw new Error('Sua sessão expirou. Abra o link do e-mail de novo.');

    await auth.setPassword(input.password);

    const profile = await accounts.updateProfile(user.id, { display_name: input.displayName });

    return mapRowToAccount(profile);
  },

  async signOut(): Promise<void> {
    await auth.signOut();
  },

  async sendPasswordReset(email: string): Promise<void> {
    await auth.sendPasswordReset(email);
  },

  /**
   * Cria a conta: usuário no serviço de auth, depois o perfil (v2: só o perfil —
   * instituição nasce em outro fluxo, via `create_institution`).
   *
   * O par `signUp` + gravação continua fora de qualquer transação porque são
   * sistemas diferentes. Se a gravação falhar, sobra um usuário autenticado sem
   * perfil — estado que `getCurrentAccount` devolve como `null`.
   *
   * ## O reenvio reaproveita o usuário que já foi criado
   *
   * Desde que o cadastro pede CPF (DV-25), a gravação do perfil falha por um
   * motivo que a pessoa corrige na hora: **CPF já cadastrado** — o banco é o
   * único que sabe, porque os perfis dos outros são invisíveis pela RLS. Sem
   * este desvio, o reenvio com o CPF corrigido chamaria `signUp` de novo e
   * esbarraria em "e-mail já em uso": o e-mail seria dela, e a conta, inútil.
   * Com a sessão do mesmo e-mail ainda aberta (e sem perfil), o reenvio só
   * grava o perfil.
   */
  async createAccount(input: NewAccountInput): Promise<Account> {
    const current = await auth.getCurrentUser();
    const isRetryOfSameSignUp =
      current !== null &&
      !current.isAnonymous &&
      current.email === input.email &&
      (await accounts.findProfile(current.id)) === null;

    if (!isRetryOfSameSignUp) {
      // CPF repetido é recusado aqui, antes de existir usuário de auth: assim o
      // erro não deixa um e-mail preso a um usuário sem perfil. A recusa do
      // banco (23505) continua valendo para a corrida entre dois cadastros.
      const cpf = input.personalData?.cpf;
      if (cpf && !(await accounts.isCpfAvailable(cpf))) {
        throw new AuthClientError('cpf_already_in_use');
      }

      await auth.signUp({ email: input.email, password: input.password });
    }

    const profile = await accounts.createAccount({
      display_name: input.displayName,
      email: input.email,
      phone_number: input.phoneNumber,
      age: null,
      gender: input.personalData?.gender ?? null,
      photo_url: DEFAULT_PROFILE_PHOTO_URL,
      is_volunteer: false,
      personal: input.personalData ? toPersonalDataRow(input.personalData) : null,
    });

    return mapRowToAccount(profile);
  },

  async listVolunteerFunctions(): Promise<VolunteerFunction[]> {
    const rows = await accounts.listVolunteerFunctions();

    return rows.map(mapRowToVolunteerFunction);
  },

  /**
   * Passo pós-cadastro (e reutilizável no perfil): marca o usuário como
   * voluntário, grava os dados pessoais quando o passo os pediu (o solicitante
   * que vira voluntário, DV-25) e substitui o conjunto de funções.
   *
   * A idade não é mais gravada aqui: com `birth_date`, o gatilho do banco a
   * deriva.
   */
  async becomeVolunteer(input: BecomeVolunteerInput): Promise<Account> {
    const user = await auth.getCurrentUser();

    if (!user) throw new Error('Sua sessão expirou. Entre novamente para continuar.');

    const profile = await accounts.updateProfile(user.id, {
      is_volunteer: true,
      ...(input.personal
        ? {
            phone_number: input.personal.phoneNumber,
            gender: input.personal.data.gender,
            ...toPersonalDataRow(input.personal.data),
          }
        : {}),
    });

    await accounts.replaceProfileFunctions(user.id, input.functionIds);

    return mapRowToAccount(profile);
  },
});

export type AuthService = ReturnType<typeof createAuthService>;
